"use client";

import { useState, useEffect, useCallback, useRef, Fragment } from "react";
import { useVisibilityRef } from "@/hooks/useVisibilityRef";
import { useDragScroll } from "@/hooks/useDragScroll";
import { useT } from "@/hooks/useT";
import HorizontalScroll from "@/app/components/HorizontalScroll";
import { useSearchParams, useRouter } from "next/navigation";
import dynamic from "next/dynamic";
import { useAuth } from "@/hooks/useAuth";
import { authFetch } from "@/lib/authFetch";
import { decodeHTMLEntities } from "@/lib/sanitize";
import { marketDataQueryKey } from "@/hooks/useMarketData";
import Link from "next/link";
import { motion, AnimatePresence } from "framer-motion";
import {
  Flame,
  Wallet,
  TrendingUp,
  TrendingDown,
  Copy,
  Trophy,
  Target,
  Zap,
  Users,
  Plus,
  ArrowUpRight,
  MessageSquare,
  Settings,
  X,
  Check,
  CheckCircle,
  ChevronDown,
  ChevronUp,
  Crown,
  Crosshair,
  Eye,
  Star,
  Newspaper,
  Sparkles,
  PenSquare,
} from "lucide-react";
import { LayoutGridIcon } from "@/app/components/icons/LayoutGridIcon";
import { LayoutListIcon } from "@/app/components/icons/LayoutListIcon";
import { EyeIcon } from "@/app/components/icons/EyeIcon";
import { UsersIcon } from "@/app/components/icons/UsersIcon";
import CreatePostModal from "@/app/components/CreatePostModal";
// Lazy-load — TradingModal pulls the Polymarket CLOB SDK (~200-300KB) and
// only renders when a user actually clicks Trade. Static import here
// statically bundled the modal into every Activity feed visit even when
// nobody clicked, defeating the dynamic split done in AppLayoutWithSidebar.
const TradingModal = dynamic(() => import("@/app/components/TradingModal"), { ssr: false });
import { useQueryClient } from "@tanstack/react-query";
import CompactEventRow from "./CompactEventRow";
import ActivityEventCard from "./ActivityEventCard";
import { useExpandedEvents } from "../hooks/useExpandedEvents";
import { useFollowedTraders } from "../hooks/useFollowedTraders";
import { useActivityFilters } from "../hooks/useActivityFilters";

// Session cache: in-memory mirror, hydrated from localStorage so a hard refresh
// or new tab still gets instant SWR render. The TTL governs both the freshness
// window for showing cached data and the persisted entries we keep on disk.
const activitySessionCache = new Map<string, { events: any[]; ts: number; offset: number }>();
const SESSION_CACHE_TTL = 5 * 60_000; // 5 minutes — generous because we always background-refresh
const STORAGE_VERSION = 1;
let cacheHydratedForUserId: string | null | undefined = undefined; // sentinel: never hydrated

function getStorageKey(userId: string | null): string {
  return `frenflow:activity:v${STORAGE_VERSION}:${userId || 'anon'}`;
}

/** Pull cached entries off localStorage into the in-memory map. Cheap (sync,
 *  ~10ms even on big payloads), called once per (userId) change before we
 *  ever touch the cache. Discards anything older than SESSION_CACHE_TTL so
 *  we never resurrect stale data on a fresh page load. */
function ensureCacheHydrated(userId: string | null) {
  if (cacheHydratedForUserId === userId) return;
  // User changed — flush the previous user's entries so we don't leak between accounts
  activitySessionCache.clear();
  cacheHydratedForUserId = userId;
  if (typeof window === 'undefined') return;
  try {
    const raw = window.localStorage.getItem(getStorageKey(userId));
    if (!raw) return;
    const obj = JSON.parse(raw);
    if (obj && typeof obj === 'object') {
      const now = Date.now();
      for (const [k, v] of Object.entries(obj as Record<string, any>)) {
        if (
          v &&
          Array.isArray(v.events) &&
          typeof v.ts === 'number' &&
          now - v.ts < SESSION_CACHE_TTL
        ) {
          activitySessionCache.set(k, v);
        }
      }
    }
  } catch {
    // Corrupt JSON or storage disabled — ignore
  }
}

let persistTimer: number | null = null;
/** Debounced persist of the in-memory cache to localStorage. Debounce so a
 *  burst of cache writes (initial load + background refresh) doesn't churn
 *  serialization on every set. */
function persistCacheDebounced(userId: string | null) {
  if (typeof window === 'undefined') return;
  if (persistTimer != null) window.clearTimeout(persistTimer);
  persistTimer = window.setTimeout(() => {
    try {
      const obj: Record<string, any> = {};
      const now = Date.now();
      for (const [k, v] of activitySessionCache.entries()) {
        if (now - v.ts < SESSION_CACHE_TTL) obj[k] = v;
      }
      window.localStorage.setItem(getStorageKey(userId), JSON.stringify(obj));
    } catch {
      // Quota exceeded or storage disabled — drop the persisted blob entirely
      // so we don't keep retrying with a payload that won't fit.
      try { window.localStorage.removeItem(getStorageKey(userId)); } catch {}
    }
  }, 500);
}

// Lazy-load CopyTraderModal - only needed when user clicks copy button
const CopyTraderModal = dynamic(
  () => import("@/app/traders/components/CopyTraderModal"),
  { ssr: false }
);

import type { EventType, ActivityEvent } from "../types";

import { getPostAttribution } from "../helpers/post-attribution";

// Filter options - Following first for personalization
// Trades = trades ejecutados + posts CON trade verificado
// Opinions = posts SIN trade verificado (solo opiniones)
const FILTER_OPTIONS = [
  { id: "following", labelKey: "activity.filter.following", icon: Users },  // Personalized feed (default if logged in)
  { id: "all", labelKey: "activity.filter.all", icon: Zap },
  { id: "trades", labelKey: "activity.filter.trades", icon: TrendingUp },      // Only user's own trades
  { id: "copies", labelKey: "activity.filter.copies", icon: Copy },            // Copy trades
  { id: "watchlist", labelKey: "activity.filter.watchlist", icon: Eye },        // Tracked wallet trades
  { id: "opinions", labelKey: "activity.filter.opinions", icon: MessageSquare }, // Posts WITHOUT verified trade
  { id: "whales", labelKey: "activity.filter.whales", icon: Wallet },
  { id: "fresh", labelKey: "activity.filter.fresh", icon: Sparkles },
  { id: "moves", labelKey: "activity.filter.moves", icon: Target },
  { id: "new", labelKey: "activity.filter.new", icon: Plus },  // New markets
];

// Map filter IDs to API types
const FILTER_TO_TYPES: Record<string, string | null> = {
  "following": null, // Uses following=true param instead
  "all": null,       // No type filter
  "trades": "user_trade,user_post_trade",
  "copies": "copy_trade",
  "watchlist": "tracker_trade",
  "opinions": "user_post_opinion",
  "moves": "market_move",
  "whales": "whale_trade",
  "fresh": "whale_trade",  // Backend fetches whale_trade, we add subtype filter below
  "new": "new_market,new_event",
};

// Categories for new_market filter
const NEW_MARKET_CATEGORIES = [
  { id: "politics", label: "Politics", emoji: "" },
  { id: "crypto", label: "Crypto", emoji: "" },
  { id: "economics", label: "Economics", emoji: "" },
  { id: "sports", label: "Sports", emoji: "" },
  { id: "trending", label: "Trending", emoji: "" },
];

// Global category filters for the feed
const CATEGORY_FILTERS = [
  { id: "politics", label: "Politics" },
  { id: "sports", label: "Sports" },
  { id: "crypto", label: "Crypto" },
  { id: "economics", label: "Economics" },
  { id: "entertainment", label: "Entertainment" },
  { id: "science", label: "Science" },
];

import { CARD_BASE } from "../helpers/styles";

interface InitialFeedData {
  filter: string;
  events: ActivityEvent[];
  hasMore: boolean;
  nextOffset: number;
}

interface ActivityFeedProps {
  embedded?: boolean;
  /** Server-rendered first page (from /activity/page.tsx). When present and
   *  the filter matches, the component starts in a "loaded" state with these
   *  events instead of the skeleton + client fetch dance — the user sees
   *  real content as soon as the HTML lands.
   *
   *  Only used on the very first render; ignored once the user navigates
   *  through filters (the SWR cache + background refresh take over). */
  initialData?: InitialFeedData | null;
  /** Filter the server already pre-fetched for. Used to gate whether
   *  initialData is honored or discarded as stale (URL changed mid-flight). */
  initialFilterFromUrl?: string;
  /** User id resolved server-side (Privy → Postgres). Available before the
   *  client-side useAuth hook finishes resolving — used as the localStorage
   *  cache key from the very first render so the SSR payload gets persisted
   *  under the correct user namespace, not under "anon". */
  userIdFromServer?: string | null;
}

export default function ActivityFeed({
  embedded = false,
  initialData,
  initialFilterFromUrl,
  userIdFromServer,
}: ActivityFeedProps) {
  const t = useT();
  const { isLoggedIn, currentUserId: clientUserId } = useAuth();
  // Prefer the server-resolved id during the auth-loading window so the
  // localStorage cache is namespaced correctly from the first render. The
  // client id takes over once useAuth settles (post-hydration).
  const currentUserId = clientUserId ?? userIdFromServer ?? null;
  const searchParams = useSearchParams();
  const router = useRouter();
  const visRef = useVisibilityRef();
  const queryClient = useQueryClient();
  const filterBarRef = useDragScroll<HTMLDivElement>();

  // Valid filter IDs for URL
  const validFilterIds = FILTER_OPTIONS.map(opt => opt.id);

  // Get initial filter — prefers what the server pre-fetched (so SSR data is
  // honored even when the URL has no ?filter=), falls back to URL, and
  // finally to "all" for anon landing.
  const getInitialFilter = () => {
    if (embedded) return "all";
    if (initialFilterFromUrl && validFilterIds.includes(initialFilterFromUrl)) {
      return initialFilterFromUrl;
    }
    const urlFilter = searchParams.get("filter");
    if (urlFilter && validFilterIds.includes(urlFilter)) {
      return urlFilter;
    }
    return "all";
  };

  const initialFilter = getInitialFilter();

  // Honor SSR data only when the URL filter matches what the server pre-fetched.
  // If the user navigated mid-flight or the URL diverged, fall back to the
  // normal client-fetch path (skeleton → fetch → render).
  const ssrUsable = !embedded
    && initialData != null
    && (!initialFilterFromUrl || initialFilterFromUrl === initialFilter)
    && initialData.filter === initialFilter
    && Array.isArray(initialData.events);

  const [events, setEvents] = useState<ActivityEvent[]>(
    ssrUsable ? initialData!.events : [],
  );
  const [newEvents, setNewEvents] = useState<ActivityEvent[]>([]);
  // IDs of events freshly streamed in via background poll while the user is
  // at the top of the feed. Used to apply the fade-in/slide-in animation
  // on auto-prepended rows so the feed actually *looks* live.
  const [freshIds, setFreshIds] = useState<Set<string>>(() => new Set());
  const [loading, setLoading] = useState(!ssrUsable);
  // Tracks whether the SSR payload has already been consumed by loadInitial.
  // Prevents the first effect run from re-fetching the same page client-side
  // and overwriting the SSR'd events with an identical refresh.
  const ssrConsumedRef = useRef(ssrUsable);
  // Show "Following" pill immediately for logged-in users (no API check delay)
  const [hasFollowing, setHasFollowing] = useState<boolean | null>(initialFilter === "following" ? true : null);
  const {
    filter,
    setFilter,
    selectedCategories,
    setSelectedCategories,
    minAmount,
    setMinAmount,
    viewMode,
    setViewMode,
  } = useActivityFilters(initialFilter, embedded);
  const [showCreateModal, setShowCreateModal] = useState(false);

  // Hover states for animated view toggle icons
  const [gridHovered, setGridHovered] = useState(false);
  const [listHovered, setListHovered] = useState(false);
  const [showCopyModal, setShowCopyModal] = useState(false);
  const [selectedTrader, setSelectedTrader] = useState<{
    username: string;
    wallet: string;
    avatar: string;
  } | null>(null);

  // Copy-trading follows + handleToggleCopy — see hooks/useFollowedTraders.ts
  const { followedTraders, fetchFollowing, handleToggleCopy } = useFollowedTraders();

  // Infinite scroll state
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [offset, setOffset] = useState(0);
  const sentinelRef = useRef<HTMLDivElement>(null);
  const ITEMS_PER_PAGE = 30;

  // Trading modal state (for quick trade from activity feed)
  const [showTradingModal, setShowTradingModal] = useState(false);
  const [tradingModalData, setTradingModalData] = useState<{
    marketSlug: string;
    outcome: "YES" | "NO";
    actualOutcome?: string;
    outcomePrice: number;
    marketQuestion: string;
    marketImage?: string | null;
    traderInfo?: {
      wallet: string;
      username?: string;
      avatar?: string;
    };
    /** Post-author referral attribution. Set ONLY when the activity is a
     *  user-authored post (user_post / user_post_trade / user_post_opinion).
     *  Must stay undefined for system events (whale_trade, market_move,
     *  resolution, pnl_*, new_market, etc.) so those do NOT credit anyone. */
    referralPostId?: string;
    referralAuthorId?: string;
  } | null>(null);

  // New market category filter state
  const [showCategoryFilter, setShowCategoryFilter] = useState(false);
  const DEFAULT_CATEGORIES = ['politics', 'crypto', 'economics', 'sports', 'trending'];
  const [enabledCategories, setEnabledCategories] = useState<string[]>(DEFAULT_CATEGORIES);
  useEffect(() => {
    try {
      const saved = localStorage.getItem('activity_new_market_categories');
      if (saved) setEnabledCategories(JSON.parse(saved));
    } catch {}
  }, []);

  // Save category preferences to localStorage
  useEffect(() => {
    if (typeof window !== 'undefined') {
      localStorage.setItem('activity_new_market_categories', JSON.stringify(enabledCategories));
    }
  }, [enabledCategories]);

  // Expanded new_event cards — see hooks/useExpandedEvents.ts
  const { expandedEvents, toggleEventExpansion } = useExpandedEvents();

  const toggleCategory = (categoryId: string) => {
    setEnabledCategories(prev =>
      prev.includes(categoryId)
        ? prev.filter(c => c !== categoryId)
        : [...prev, categoryId]
    );
  };

  // Sanitize wallet address - some API responses include timestamps like "0x...abc-1762452016082"
  const sanitizeWalletAddress = (address: string | undefined | null): string | undefined => {
    if (!address) return undefined;
    // Extract valid Ethereum address (42 chars starting with 0x)
    const match = address.match(/^(0x[a-fA-F0-9]{40})/);
    if (match) return match[1].toLowerCase();
    // Return undefined if not a valid wallet format
    if (!/^0x[a-fA-F0-9]{40}$/.test(address)) return undefined;
    return address.toLowerCase();
  };

  // Open trading modal for quick copy trade
  const openTradingModal = useCallback((event: ActivityEvent) => {
    if (!event.market_slug) return;

    const outcomeNormalized = event.outcome?.toLowerCase() || 'yes';
    const isYes = outcomeNormalized === 'yes' || outcomeNormalized === 'up';

    // Extract trader info from event
    // For user_trade: source_wallet, source_username, metadata.display_name, metadata.user_image
    // For whale_trade: source_wallet/metadata.wallet_address, metadata.wallet_name, metadata.wallet_profile_image
    // Sanitize wallet to handle malformed addresses with timestamps
    const rawWallet = event.source_wallet || event.metadata?.wallet_address;
    const wallet = sanitizeWalletAddress(rawWallet);
    const traderInfo = wallet ? {
      wallet,
      username: event.source_username || event.metadata?.display_name || event.metadata?.wallet_name || undefined,
      avatar: event.metadata?.user_image || event.metadata?.wallet_profile_image || undefined,
    } : undefined;

    // Ensure outcomePrice is always a valid number (event.price may be string or undefined)
    const price = Number(event.price);
    const safeOutcomePrice = !isNaN(price) && price > 0 ? price : 0.5;

    // Post-author referral attribution: only on user-authored posts. System
    // events (whale_trade, market_move, etc.) must NOT carry attribution.
    const isUserPost =
      event.source_table === 'post' &&
      (event.event_type === 'user_post' ||
        event.event_type === 'user_post_trade' ||
        event.event_type === 'user_post_opinion');

    setTradingModalData({
      marketSlug: event.market_slug,
      outcome: isYes ? "YES" : "NO",
      actualOutcome: event.outcome,
      outcomePrice: safeOutcomePrice,
      marketQuestion: decodeHTMLEntities(event.market_title || '') || "Unknown Market",
      marketImage: event.market_image,
      traderInfo,
      referralPostId: isUserPost ? event.id : undefined,
      referralAuthorId: isUserPost ? event.source_user_id : undefined,
    });
    setShowTradingModal(true);
  }, []);

  // Open copy modal for a trader
  // Supports: regular events (source_wallet) and copy_trade events (leader_address)
  const openCopyModal = useCallback((event: ActivityEvent) => {
    // For copy_trade events, we want to copy the leader (not the copier)
    const isCopyTrade = event.event_type === 'copy_trade';
    const rawWallet = isCopyTrade
      ? event.metadata?.leader_address
      : (event.source_wallet || event.metadata?.wallet_address);
    const wallet = sanitizeWalletAddress(rawWallet);
    if (!wallet) return;

    setSelectedTrader({
      username: isCopyTrade
        ? (event.metadata?.leader_username || 'Unknown')
        : (event.source_username || event.metadata?.username || 'Unknown'),
      wallet: wallet,
      avatar: isCopyTrade
        ? (event.metadata?.leader_image || '/frenpfp.png')
        : (event.metadata?.user_image || event.metadata?.wallet_profile_image || '/frenpfp.png'),
    });
    setShowCopyModal(true);
  }, []);

  // Extract grouping key from market title for deduplication
  // Groups markets like "Will the price of Ethereum be between $X and $Y on February 3?"
  const getMarketGroupKey = useCallback((title: string | undefined): string | null => {
    if (!title) return null;

    // Pattern: "Will the price of [ASSET] be ... on [DATE]?"
    const priceMatch = title.match(/Will the price of (\w+) be .+ on (.+)\?/i);
    if (priceMatch) {
      return `price_${priceMatch[1].toLowerCase()}_${priceMatch[2].toLowerCase().replace(/\s+/g, '_')}`;
    }

    // Pattern: "Will [NAME] win the [ELECTION]?"
    const electionMatch = title.match(/Will .+ win the (.+by-election|.+election)\?/i);
    if (electionMatch) {
      return `election_${electionMatch[1].toLowerCase().replace(/\s+/g, '_')}`;
    }

    // Pattern: "[ASSET] Up or Down" (short-term spam - will be filtered anyway)
    const upDownMatch = title.match(/^(\w+) Up or Down/i);
    if (upDownMatch) {
      return `updown_${upDownMatch[1].toLowerCase()}`;
    }

    return null;
  }, []);

  // Filter and sort helper
  const filterAndSortEvents = useCallback((fetchedEvents: ActivityEvent[]) => {
    // STEP 1: Group new_market events to avoid redundant cards
    // First try event_id from metadata, then fall back to title pattern matching
    const groupedEvents: ActivityEvent[] = [];
    const newMarketsByGroupKey = new Map<string, ActivityEvent[]>();
    const newMarketsUngrouped: ActivityEvent[] = [];

    for (const event of fetchedEvents) {
      if (event.event_type === 'new_market') {
        // Try event_id first (from backend), then title pattern
        const eventId = event.metadata?.event_id;
        const titleKey = getMarketGroupKey(event.market_title);
        const groupKey = eventId ? `eid_${eventId}` : titleKey;

        if (groupKey) {
          const group = newMarketsByGroupKey.get(groupKey) || [];
          group.push(event);
          newMarketsByGroupKey.set(groupKey, group);
        } else {
          newMarketsUngrouped.push(event);
        }
      } else {
        groupedEvents.push(event);
      }
    }

    // Create grouped new_event for each group with multiple markets
    for (const [groupKey, markets] of newMarketsByGroupKey) {
      if (markets.length === 1) {
        // Only one market, keep as new_market
        groupedEvents.push(markets[0]);
      } else {
        // Multiple markets - create a virtual new_event
        const firstMarket = markets[0];

        // Generate a nice title for the group
        let groupTitle = firstMarket.market_title || '';
        const priceMatch = groupTitle.match(/Will the price of (\w+) be .+ on (.+)\?/i);
        if (priceMatch) {
          groupTitle = `${priceMatch[1]} price on ${priceMatch[2]}`;
        }
        const electionMatch = groupTitle.match(/Will .+ win the (.+)\?/i);
        if (electionMatch) {
          groupTitle = electionMatch[1];
        }

        const groupedEvent: ActivityEvent = {
          ...firstMarket,
          id: `grouped_${groupKey}`, // Virtual ID for the group
          event_type: 'new_event',
          market_title: groupTitle,
          headline: `🆕 New event with ${markets.length} markets`,
          metadata: {
            ...firstMarket.metadata,
            market_count: markets.length,
            markets: markets.map(m => ({
              slug: m.market_slug,
              question: m.market_title,
            })),
          },
        };
        groupedEvents.push(groupedEvent);
      }
    }

    // Add ungrouped new_market events
    groupedEvents.push(...newMarketsUngrouped);

    // STEP 1.5: Group repeated trades from same user + market + side within 30 min
    const TRADE_GROUP_WINDOW_MS = 30 * 60 * 1000;
    const TRADE_GROUP_MIN = 3; // minimum trades to trigger grouping
    const tradeGroupTypes = new Set(["user_trade", "copy_trade", "whale_trade", "tracker_trade"]);

    const tradesByKey = new Map<string, ActivityEvent[]>();
    const afterTradeGrouping: ActivityEvent[] = [];

    for (const event of groupedEvents) {
      if (tradeGroupTypes.has(event.event_type) && event.market_slug) {
        const key = `${event.source_wallet || event.source_user_id || ''}_${event.market_slug}_${event.side || 'BUY'}`;
        const group = tradesByKey.get(key) || [];
        group.push(event);
        tradesByKey.set(key, group);
      } else {
        afterTradeGrouping.push(event);
      }
    }

    const flushTradeBatch = (batch: ActivityEvent[]) => {
      if (batch.length < TRADE_GROUP_MIN) {
        afterTradeGrouping.push(...batch);
        return;
      }
      const totalSize = batch.reduce((sum, e) => sum + (e.size_usd || 0), 0);
      const totalShares = batch.reduce((sum, e) => sum + (Number(e.shares) || 0), 0);
      const latest = batch[batch.length - 1];

      // Same-datum-two-sources problem (Jan 2026 incident pattern from CLAUDE.md):
      // individual trade rows render `Number(event.price)` straight from the
      // backend, but the grouped row used to recompute `totalSize / totalShares`.
      // Because the backend may round `price` independently from `size_usd /
      // shares`, the same underlying trade displayed two slightly different
      // prices depending on whether it was shown alone or as part of a batch.
      //
      // Fix: use the latest trade's price (the same value the individual row
      // would show) for the grouped row's `price` field. Keep the weighted
      // average in metadata so downstream consumers (tooltips, share modals)
      // can surface it without conflicting with the row's primary price.
      const latestPrice = Number(latest.price) || 0;
      const weightedAvg = totalShares > 0 ? totalSize / totalShares : latestPrice;

      afterTradeGrouping.push({
        ...latest,
        id: `grouped_trade_${latest.id}`,
        size_usd: totalSize,
        shares: totalShares,
        price: latestPrice,
        event_at: latest.event_at,
        metadata: {
          ...latest.metadata,
          grouped_trade_count: batch.length,
          grouped_weighted_avg_price: weightedAvg > 0 && weightedAvg <= 1 ? weightedAvg : null,
          grouped_trades: batch.map(t => ({
            id: t.id, size_usd: t.size_usd, price: t.price, shares: t.shares, event_at: t.event_at,
          })),
        },
      });
    };

    for (const [, trades] of tradesByKey) {
      trades.sort((a, b) => new Date(a.event_at).getTime() - new Date(b.event_at).getTime());
      let currentBatch: ActivityEvent[] = [trades[0]];
      for (let i = 1; i < trades.length; i++) {
        const timeDiff = new Date(trades[i].event_at).getTime() - new Date(currentBatch[0].event_at).getTime();
        if (timeDiff <= TRADE_GROUP_WINDOW_MS) {
          currentBatch.push(trades[i]);
        } else {
          flushTradeBatch(currentBatch);
          currentBatch = [trades[i]];
        }
      }
      flushTradeBatch(currentBatch);
    }

    // STEP 2: Filter out spammy "Up or Down" short-term markets
    // Only apply category filter when viewing "New" tab specifically
    let filtered = afterTradeGrouping.filter((e: ActivityEvent) => {
      // Fresh Wallets tab: only show fresh_whale events
      if (filter === 'fresh') {
        return e.event_subtype === 'fresh_whale' || e.metadata?.is_fresh_wallet === true;
      }
      if (e.event_type === 'new_market' || e.event_type === 'new_event') {
        // Skip "Up or Down" crypto price markets (every 15 min spam)
        if (e.market_title?.includes('Up or Down')) return false;
        // Only apply category filter on the "New" tab, show all in "All" tab
        if (filter === 'new') {
          const category = (e.metadata?.category || 'trending').toLowerCase();
          // Only filter out known categories that user explicitly disabled
          // Unknown categories (from Gamma API like "Pop Culture", "Science") always pass through
          const knownCategoryIds = NEW_MARKET_CATEGORIES.map(c => c.id);
          if (knownCategoryIds.includes(category) && !enabledCategories.includes(category)) return false;
        }
      }
      return true;
    });

    // SOCIAL LAYER PRIORITY: Traders first, market noise last
    const getTierPriority = (e: ActivityEvent) => {
      if (e.event_type === 'user_trade' || e.event_type === 'copy_trade' || e.event_type === 'user_post_trade') return 0;
      if (e.event_type === 'user_post' && e.metadata?.trade_data) return 0;
      if (e.event_type === 'whale_trade' || e.event_type === 'tracker_trade') return 1;
      if (e.event_type === 'pnl_win' || e.event_type === 'pnl_loss') return 1;
      if (e.event_type === 'follow') return 1;
      if (e.event_type === 'user_post_opinion') return 2;
      if (e.event_type === 'blog_article') return 2;
      if (e.event_type === 'market_move' || e.event_type === 'resolution') return 3;
      if (e.event_type === 'user_post') return 3;
      if (e.event_type === 'new_market' || e.event_type === 'new_event') return 4;
      return 5;
    };

    // Personal feeds (Following / Watchlist) read chronologically — newest
    // activity first regardless of type — so a freshly-posted thesis isn't
    // buried below a followed trader's older trades. Tier-priority (surface
    // trades over market noise) stays for the discovery feeds (All / New / …).
    // event_at can be missing on some event shapes → fall back to created_at
    // and guard NaN so the comparator never collapses to insertion order
    // (which surfaced posts oldest-first). 2026-06-09.
    const tsOf = (e: ActivityEvent) => {
      const ts = new Date(e.event_at || e.created_at || 0).getTime();
      return Number.isFinite(ts) ? ts : 0;
    };
    const isPersonalFeed = filter === 'following' || filter === 'watchlist';
    return filtered.sort((a, b) => {
      if (!isPersonalFeed) {
        const tierA = getTierPriority(a);
        const tierB = getTierPriority(b);
        if (tierA !== tierB) return tierA - tierB;
      }
      return tsOf(b) - tsOf(a);
    });
  }, [filter, enabledCategories]);

  // Build query params helper
  const buildQueryParams = useCallback((pageOffset: number = 0) => {
    let queryParams = `limit=${ITEMS_PER_PAGE}&offset=${pageOffset}`;

    if (filter === "following") {
      queryParams += "&types=user_trade,copy_trade,user_post,pnl_win,pnl_loss,tracker_trade&following=true";
    } else if (filter === "watchlist") {
      // Watchlist = tracker_trade filtered by user's tracked wallets (requires following=true for backend filter)
      queryParams += "&types=tracker_trade&following=true";
    } else if (filter === "all") {
      queryParams += "&types=user_trade,copy_trade,user_post,market_move,resolution,pnl_win,pnl_loss,follow,whale_trade,tracker_trade,new_market,new_event";
    } else {
      // Use the mapping to get API types
      const apiTypes = FILTER_TO_TYPES[filter];
      if (apiTypes) {
        queryParams += `&types=${apiTypes}`;
      }
    }
    // Fresh filter: add subtype to avoid fetching 61K whale trades and filtering client-side
    if (filter === 'fresh') {
      queryParams += '&subtype=fresh_whale';
    }
    if (selectedCategories.length > 0) {
      queryParams += `&category=${selectedCategories.join(',')}`;
    }
    if (minAmount > 0) {
      queryParams += `&minAmount=${minAmount}`;
    }
    return queryParams;
  }, [filter, selectedCategories, minAmount]);

  // Use authFetch for filters that need user identity (following, watchlist)
  const needsAuth = filter === "following" || filter === "watchlist";
  const activityFetch = useCallback((url: string) => {
    return needsAuth ? authFetch(url) : fetch(url);
  }, [needsAuth]);

  // Batch-prefetch market data for activity cards (avoids N+1 individual fetches).
  // Slugs NOT returned by the batch (archived/resolved markets) get their
  // React Query cache entry pre-primed with a MarketNotFoundError via
  // prefetchQuery, so individual cards resolve from cache instead of each
  // firing their own 404 request (which was flooding the console).
  // Hydrates the React Query market cache for the events about to render.
  // Runs in two phases: a synchronous inline seed from `event.market_data`
  // (the common path now that /activity/stream ships market data inline),
  // then a fire-and-forget batch fetch only for slugs that didn't come back
  // inline. Returns void synchronously — callers DON'T await it. setEvents
  // can fire immediately after; cards read the already-seeded cache on
  // mount and don't trigger their own fetches.
  const prefetchMarkets = useCallback((activityEvents: ActivityEvent[]): void => {
    const slugs = [...new Set(
      activityEvents
        .map(e => e.market_slug)
        .filter((s): s is string => !!s && !s.toLowerCase().startsWith('kx'))
    )];

    // ── Phase 1: synchronous inline seed ─────────────────────────────────
    // On a warm markets_cache this covers 100% of slugs and the async phase
    // is a no-op. setQueryData is sync so cards mounted in the same React
    // commit see the data on first render — no skeleton flash.
    //
    // IMPORTANT: useMarketData uses a 3-element queryKey ["market", slug,
    // source]. Seeding with 2 elements never matches and every card refetches
    // on mount — that flood was producing intermittent 500s from the proxy
    // and showing "Failed to fetch market data" red banners on first load.
    // Use marketDataQueryKey() so the seed slot lines up with the consumer.
    const seededSlugs = new Set<string>();
    for (const e of activityEvents) {
      const inline = (e as any).market_data;
      if (!e.market_slug || !inline) continue;
      const key = marketDataQueryKey(e.market_slug, e.metadata?.source as any);
      if (!queryClient.getQueryData(key)) {
        queryClient.setQueryData(key, inline, { updatedAt: Date.now() });
      }
      seededSlugs.add(e.market_slug);
    }

    const uncached = slugs.filter(slug =>
      !seededSlugs.has(slug) && !queryClient.getQueryData(marketDataQueryKey(slug))
    );
    if (uncached.length === 0) return;

    // ── Phase 2: async fallback (fire-and-forget) ────────────────────────
    // Only triggered for slugs the backend couldn't enrich (archived markets
    // or rows missing from markets_cache). Runs in the background while the
    // feed renders. Cards for these slugs will show the loading state for a
    // moment then re-render once the batch resolves.
    void (async () => {
      try {
        const res = await fetch('/api/polymarket/market/batch', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ slugs: uncached }),
        });
        if (!res.ok) return;
        const data = await res.json();
        const returned = new Set<string>();
        if (data.markets) {
          for (const [slug, market] of Object.entries(data.markets)) {
            if (market) {
              queryClient.setQueryData(marketDataQueryKey(slug), market, { updatedAt: Date.now() });
              returned.add(slug);
            }
          }
        }
        const missing = uncached.filter((slug) => !returned.has(slug));
        if (missing.length > 0) {
          const { MarketNotFoundError } = await import("@/hooks/useMarketData");
          await Promise.all(
            missing.map((slug) =>
              queryClient
                .prefetchQuery({
                  queryKey: marketDataQueryKey(slug),
                  queryFn: async () => { throw new MarketNotFoundError(); },
                  staleTime: 15 * 60_000,
                  retry: false,
                })
                .catch(() => {})
            )
          );
        }
      } catch {
        // Silently fail — individual cards fetch on their own as last resort
      }
    })();
  }, [queryClient]);

  // Fetch events - initial load or refresh
  const fetchEvents = useCallback(async (isBackground = false) => {
    try {
      const queryParams = buildQueryParams(0);
      const res = await activityFetch(`/api/activity/stream?${queryParams}`);
      const data = await res.json();
      if (data.success) {
        const fetchedEvents = filterAndSortEvents(data.events || []);

        if (isBackground && events.length > 0) {
          // Background poll - check for new events
          const existingIds = new Set(events.map(e => e.id));
          const newOnes = fetchedEvents.filter((e: ActivityEvent) => !existingIds.has(e.id));
          if (newOnes.length > 0) {
            // Twitter-style "live" feel: if the user is parked at the top
            // (scrolled within 200px of page top), inject new events directly
            // with a fade-in animation. If scrolled deeper, buffer them into
            // `newEvents` so the existing "Show N new" pill surfaces — keeps
            // older content steady under the user's eye.
            const atTop = typeof window !== 'undefined' && window.scrollY < 200;
            if (atTop) {
              const newIds = newOnes.map((e: ActivityEvent) => e.id);
              setFreshIds(prev => {
                const next = new Set(prev);
                newIds.forEach((id: string) => next.add(id));
                return next;
              });
              setEvents(prev => {
                // Dedup (poll could include items already in newEvents from a
                // previous mid-scroll buffering session).
                const existing = new Set(prev.map(e => e.id));
                const filtered = newOnes.filter((e: ActivityEvent) => !existing.has(e.id));
                return [...filtered, ...prev];
              });
              // Strip the fresh marker after the animation lands. 600ms
              // matches the longest entrance (500ms duration + breathing room).
              window.setTimeout(() => {
                setFreshIds(prev => {
                  const next = new Set(prev);
                  newIds.forEach((id: string) => next.delete(id));
                  return next;
                });
              }, 600);
            } else {
              setNewEvents(prev => {
                const combined = [...newOnes, ...prev];
                const seen = new Set<string>();
                return combined.filter(e => {
                  if (seen.has(e.id)) return false;
                  seen.add(e.id);
                  return true;
                });
              });
            }
          }
        } else {
          // Await the batch prefetch BEFORE rendering cards. Otherwise the cards
          // mount, fire individual /api/polymarket/market/:slug fetches for
          // archived markets, and flood the console with 404s before the batch
          // has seeded the MarketNotFoundError sentinel into React Query cache.
          prefetchMarkets(fetchedEvents);
          setEvents(fetchedEvents);
          setNewEvents([]);
          setOffset(data.nextOffset || data.events?.length || 0);
          setHasMore(data.hasMore ?? (data.events?.length >= ITEMS_PER_PAGE));
        }
      }
    } catch (e) {
    } finally {
      setLoading(false);
    }
  }, [buildQueryParams, filterAndSortEvents, events, prefetchMarkets]);

  // Load more events for infinite scroll
  const loadMoreEvents = useCallback(async () => {
    if (loadingMore || !hasMore) return;

    setLoadingMore(true);

    try {
      const queryParams = buildQueryParams(offset);
      const res = await activityFetch(`/api/activity/stream?${queryParams}`);
      const data = await res.json();

      if (data.success) {
        const fetchedEvents = filterAndSortEvents(data.events || []);
        const newOffset = data.nextOffset || offset + (data.events?.length || 0);
        const moreAvailable = data.hasMore ?? (data.events?.length >= ITEMS_PER_PAGE);

        // Dedupe with existing events
        const existingIds = new Set(events.map(e => e.id));
        const uniqueNewEvents = fetchedEvents.filter((e: ActivityEvent) => !existingIds.has(e.id));

        if (uniqueNewEvents.length > 0) {
          // Same no-flood rule as the fresh-load path: warm the cache before
          // the new cards mount.
          prefetchMarkets(uniqueNewEvents);
          setEvents(prev => [...prev, ...uniqueNewEvents]);
        }

        setOffset(newOffset);
        setHasMore(moreAvailable);
      }
    } catch (e) {
    } finally {
      setLoadingMore(false);
    }
  }, [buildQueryParams, filterAndSortEvents, events, offset, loadingMore, hasMore, prefetchMarkets]);

  // Track if we've done the initial auto-switch check
  const didAutoSwitchRef = useRef(false);

  // Show "Following" pill for all logged-in users immediately (no async check).
  // If they don't follow anyone, the feed shows an empty-state message — no need to hide the pill.
  useEffect(() => {
    if (!isLoggedIn) {
      setHasFollowing(false);
      return;
    }

    setHasFollowing(true);

    // Auto-switch to following tab on first mount if no filter in URL
    if (!didAutoSwitchRef.current) {
      didAutoSwitchRef.current = true;
      const urlFilter = typeof window !== 'undefined' ? new URLSearchParams(window.location.search).get("filter") : null;
      if (!urlFilter) {
        setFilter("following");
      }
    }
  }, [isLoggedIn, setFilter]);

  // Initial load when filter or categories change
  useEffect(() => {
    let isCancelled = false;

    const loadInitial = async () => {
      setNewEvents([]);
      setHasMore(true);

      // Pull persisted entries off localStorage before the first cache lookup so a
      // hard refresh / new tab still hits the SWR fast-path instead of falling through
      // to the spinner + 5-10s network wait.
      ensureCacheHydrated(currentUserId);

      const cacheKey = `${filter}:${selectedCategories.join(',')}`;

      // ── SSR fast path ────────────────────────────────────────────────
      // First mount with server-rendered initial data: seed market cache,
      // populate the session cache, and skip the network fetch entirely —
      // the user is already looking at real cards. After this we flip the
      // ref off so subsequent filter changes/refreshes use the normal path.
      if (ssrConsumedRef.current && initialData) {
        ssrConsumedRef.current = false;
        prefetchMarkets(initialData.events);
        setHasMore(initialData.hasMore);
        setOffset(initialData.nextOffset);
        // Persist the SSR payload to the SWR cache so a back-nav within the
        // session is also instant without re-doing the SSR work.
        activitySessionCache.set(cacheKey, {
          events: initialData.events,
          ts: Date.now(),
          offset: initialData.nextOffset,
        });
        persistCacheDebounced(currentUserId);
        return;
      }

      // Check session cache first — show cached data instantly, then refresh in background.
      // Skip cache seed if it's empty: a previously-failed fetch could have stored [] and
      // then the tab would render the empty-state flash before the background refresh completes.
      // Better to show the skeleton briefly and render fresh data than flash "No copy trades".
      const cached = activitySessionCache.get(cacheKey);
      if (cached && cached.events.length > 0 && Date.now() - cached.ts < SESSION_CACHE_TTL) {
        setEvents(cached.events);
        setOffset(cached.offset);
        setLoading(false);
        // Background refresh
        try {
          const queryParams = buildQueryParams(0);
          const res = await activityFetch(`/api/activity/stream?${queryParams}`);
          const data = await res.json();
          if (isCancelled) return;
          if (data.success) {
            const fetchedEvents = filterAndSortEvents(data.events || []);
            prefetchMarkets(fetchedEvents);
            if (isCancelled) return;
            setEvents(fetchedEvents);
            const newOffset = data.nextOffset || data.events?.length || 0;
            setOffset(newOffset);
            setHasMore(data.hasMore ?? (data.events?.length >= ITEMS_PER_PAGE));
            activitySessionCache.set(cacheKey, { events: fetchedEvents, ts: Date.now(), offset: newOffset });
            persistCacheDebounced(currentUserId);
          }
        } catch (e) {}
        return;
      }

      setLoading(true);
      setOffset(0);

      try {
        const queryParams = buildQueryParams(0);
        const res = await activityFetch(`/api/activity/stream?${queryParams}`);
        const data = await res.json();

        if (isCancelled) return;

        if (data.success) {
          const fetchedEvents = filterAndSortEvents(data.events || []);
          prefetchMarkets(fetchedEvents);
          if (isCancelled) return;
          setEvents(fetchedEvents);
          const newOffset = data.nextOffset || data.events?.length || 0;
          setOffset(newOffset);
          setHasMore(data.hasMore ?? (data.events?.length >= ITEMS_PER_PAGE));
          activitySessionCache.set(cacheKey, { events: fetchedEvents, ts: Date.now(), offset: newOffset });
          persistCacheDebounced(currentUserId);
        }
      } catch (e) {
      } finally {
        if (!isCancelled) {
          setLoading(false);
        }
      }
    };

    loadInitial();

    return () => {
      isCancelled = true;
    };
  }, [filter, enabledCategories, selectedCategories, buildQueryParams, filterAndSortEvents, currentUserId]);

  // Keep the session cache in lock-step with `events`. Without this the cache
  // captures the initial fetch only, then drifts when background-poll prepends,
  // `loadMoreEvents` appends, or "Show N new" pill flushes the buffer — and on
  // the next re-mount (filter toggled back, currentUserId resolves post-hydration)
  // the user briefly sees the stale snapshot before the background refresh fills
  // in the fresh data. Pika 2026-05-22: reported as "actualizo, salen los
  // nuevos resultados y se cambian por viejos solos".
  useEffect(() => {
    if (loading) return;            // skeleton phase — don't persist []
    if (events.length === 0) return; // empty filters: keep prior good snapshot
    const cacheKey = `${filter}:${selectedCategories.join(',')}`;
    activitySessionCache.set(cacheKey, { events, ts: Date.now(), offset });
    persistCacheDebounced(currentUserId);
  }, [events, offset, filter, selectedCategories, loading, currentUserId]);

  // Background prefetch of the most-likely-next tabs so switching pestañas is instant.
  // Runs once after the initial load completes, paces requests with requestIdleCallback,
  // and only fetches a small payload (limit=10) per tab — enough to seed the cache so
  // the SWR fast-path lights up the next time the user clicks that filter, without
  // hammering the backend with 10 parallel feed queries on mount.
  const prefetchedTabsRef = useRef(false);
  useEffect(() => {
    if (loading) return; // wait for the visible tab to settle first
    if (prefetchedTabsRef.current) return;
    prefetchedTabsRef.current = true;
    if (typeof window === 'undefined') return;

    // Hot tabs the average user pings within the same session. Watchlist + opinions
    // intentionally excluded: low traffic, easy to fetch lazily on click.
    const hotFilters = ['all', 'following', 'trades', 'whales'].filter(f => f !== filter);

    const buildPrefetchParams = (filterId: string): { url: string; needsAuth: boolean } => {
      let qp = `limit=10&offset=0`;
      let needsAuth = false;
      if (filterId === 'following') {
        qp += '&types=user_trade,copy_trade,user_post,pnl_win,pnl_loss,tracker_trade&following=true';
        needsAuth = true;
      } else if (filterId === 'watchlist') {
        qp += '&types=tracker_trade&following=true';
        needsAuth = true;
      } else if (filterId === 'all') {
        qp += '&types=user_trade,copy_trade,user_post,market_move,resolution,pnl_win,pnl_loss,follow,whale_trade,tracker_trade,new_market,new_event';
      } else {
        const apiTypes = FILTER_TO_TYPES[filterId];
        if (apiTypes) qp += `&types=${apiTypes}`;
      }
      return { url: `/api/activity/stream?${qp}`, needsAuth };
    };

    const prefetchOne = async (filterId: string) => {
      // "Following" without auth would just return empty — skip to save a round trip.
      if ((filterId === 'following' || filterId === 'watchlist') && !isLoggedIn) return;
      const cacheKey = `${filterId}:`;
      const existing = activitySessionCache.get(cacheKey);
      if (existing && existing.events.length > 0 && Date.now() - existing.ts < SESSION_CACHE_TTL) return;
      try {
        const { url, needsAuth: na } = buildPrefetchParams(filterId);
        const res = await (na ? authFetch(url) : fetch(url));
        const data = await res.json();
        if (data?.success && Array.isArray(data.events)) {
          activitySessionCache.set(cacheKey, {
            events: data.events,
            ts: Date.now(),
            offset: data.nextOffset || data.events.length,
          });
          persistCacheDebounced(currentUserId);
        }
      } catch {
        // Prefetch is best-effort — failures fall back to the normal fetch on tab click
      }
    };

    const runSequential = async () => {
      for (const f of hotFilters) {
        await prefetchOne(f);
      }
    };

    const ric = (window as any).requestIdleCallback as
      | ((cb: () => void, opts?: { timeout: number }) => number)
      | undefined;
    if (ric) {
      ric(() => { runSequential(); }, { timeout: 3000 });
    } else {
      window.setTimeout(() => { runSequential(); }, 500);
    }
  }, [loading, filter, isLoggedIn, currentUserId]);

  // Infinite scroll with IntersectionObserver (disabled when embedded)
  useEffect(() => {
    if (embedded) return;
    const sentinel = sentinelRef.current;
    if (!sentinel) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loading && !loadingMore) {
          loadMoreEvents();
        }
      },
      { rootMargin: "200px" }
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasMore, loading, loadingMore, loadMoreEvents, embedded]);

  // Background poll every 10 seconds for new content (disabled when embedded
  // or tab hidden). New events auto-prepend if the user is at the top of the
  // feed, otherwise they buffer behind the "Show N new" pill — handled in
  // fetchEvents → isBackground branch.
  useEffect(() => {
    if (embedded) return;
    const interval = setInterval(() => {
      if (visRef.current) fetchEvents(true);
    }, 10000);
    return () => clearInterval(interval);
  }, [fetchEvents, embedded]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load new events into feed
  const loadNewEvents = () => {
    setEvents(prev => [...newEvents, ...prev]);
    setNewEvents([]);
    // Scroll to top
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Handle vote on posts and activity events. useCallback so EventCard's
  // onVote prop ref stays stable across re-renders — together with the other
  // useCallback'd handlers this is what makes React.memo on EventCard
  // actually cut re-renders during polls and unrelated state changes.
  const handleVote = useCallback(async (eventId: string, voteType: number, sourceTable: string) => {
    if (!isLoggedIn) return;

    // Find the event
    const event = events.find(e => e.id === eventId);
    if (!event) return;

    // Get current vote
    const currentVote = event.user_vote || 0;
    const newVoteType = currentVote === voteType ? 0 : voteType;

    try {
      if (sourceTable === 'post') {
        // Use posts like endpoint
        const response = await authFetch("/api/posts/like", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ postId: eventId, voteType: newVoteType }),
        });

        if (response.ok) {
          const data = await response.json();
          setEvents(prev => prev.map(e =>
            e.id === eventId
              ? { ...e, likes: data.likes, dislikes: data.dislikes, user_vote: data.userVote }
              : e
          ));
        }
      } else if (sourceTable === 'activity_event') {
        // Use activity react endpoint
        // Map: like (1) → 'fire', dislike (-1) → 'skull'
        const reaction = voteType === 1 ? 'fire' : 'skull';
        const isRemoving = currentVote === voteType; // Toggle off

        const response = await authFetch(`/api/activity/${eventId}/react`, {
          method: isRemoving ? "DELETE" : "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ reaction }),
        });

        if (response.ok) {
          // Optimistic update
          setEvents(prev => prev.map(e => {
            if (e.id !== eventId) return e;

            const currentLikes = e.likes || e.reactions?.fire || 0;
            const currentDislikes = e.dislikes || e.reactions?.skull || 0;

            let newLikes = currentLikes;
            let newDislikes = currentDislikes;
            let newUserVote = newVoteType;

            if (voteType === 1) {
              // Like action
              if (isRemoving) {
                newLikes = Math.max(0, currentLikes - 1);
              } else {
                newLikes = currentLikes + 1;
                // If was dislike, remove it
                if (currentVote === -1) {
                  newDislikes = Math.max(0, currentDislikes - 1);
                }
              }
            } else {
              // Dislike action
              if (isRemoving) {
                newDislikes = Math.max(0, currentDislikes - 1);
              } else {
                newDislikes = currentDislikes + 1;
                // If was like, remove it
                if (currentVote === 1) {
                  newLikes = Math.max(0, currentLikes - 1);
                }
              }
            }

            return {
              ...e,
              likes: newLikes,
              dislikes: newDislikes,
              reactions: { ...e.reactions, fire: newLikes, skull: newDislikes },
              user_vote: newUserVote,
            };
          }));
        }
      }
    } catch (error) {
    }
  }, [isLoggedIn, events]);

  return (
    <>
      <CreatePostModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
      />

      {selectedTrader && (
        <CopyTraderModal
          isOpen={showCopyModal}
          onClose={() => {
            setShowCopyModal(false);
            setSelectedTrader(null);
            fetchFollowing();
          }}
          trader={selectedTrader}
        />
      )}

      {tradingModalData && (
        <TradingModal
          isOpen={showTradingModal}
          onClose={() => {
            setShowTradingModal(false);
            setTradingModalData(null);
          }}
          marketSlug={tradingModalData.marketSlug}
          outcome={tradingModalData.outcome}
          actualOutcome={tradingModalData.actualOutcome}
          outcomePrice={tradingModalData.outcomePrice}
          marketQuestion={tradingModalData.marketQuestion}
          marketImage={tradingModalData.marketImage}
          traderInfo={tradingModalData.traderInfo}
          referralPostId={tradingModalData.referralPostId ?? null}
          referralAuthorId={tradingModalData.referralAuthorId ?? null}
          onCopyTrader={tradingModalData.traderInfo ? (() => {
            // Capture trader info NOW to avoid stale closure after modal closes
            const capturedTrader = {
              username: tradingModalData.traderInfo!.username || 'Unknown',
              wallet: tradingModalData.traderInfo!.wallet,
              avatar: tradingModalData.traderInfo!.avatar || '/frenpfp.png',
            };
            return () => {
              setSelectedTrader(capturedTrader);
              setShowCopyModal(true);
            };
          })() : undefined}
        />
      )}

      <div className={embedded ? "min-h-[600px]" : "pb-24 lg:pb-4 min-h-screen"}>
        {/* Filter bar — mirrors /markets exactly: fixed under the mobile
            header on mobile, sticky on desktop. Only the scrollable
            filter pills live here so the layout is identical to the
            categories bar in /markets (px-4 padding, mobile-cat-bar
            class for the global mobile-header offset). The view-mode
            toggle stays inside on desktop where it has room; on mobile
            it lives below in a secondary row so it doesn't compete
            with the scroll fade on the right edge.
            Refresh button removed — newEvents pill already surfaces
            new activity automatically; the explicit refresh was
            redundant.  Publish moved to a FAB at bottom-right. */}
        <div className={`${embedded ? "" : "fixed top-12 left-0 right-0 z-30 lg:sticky lg:top-0 lg:left-auto lg:right-auto"} backdrop-blur-[20px] backdrop-saturate-[1.8] bg-white/60 dark:bg-[#1c1c1e]/50 border-b border-gray-200/30 dark:border-[#1A1A1A] lg:backdrop-blur-none lg:bg-gray-50 lg:dark:bg-[#0f0f12] lg:border-gray-200 lg:dark:border-[#1A1A1A] mobile-cat-bar`}>
          <div className="w-full px-4 lg:px-6 py-2">
            <HorizontalScroll
              ref={filterBarRef}
              className="cursor-grab active:cursor-grabbing select-none"
              fadeFrom="bg-white dark:bg-[#1c1c1e] lg:bg-gray-50 lg:dark:bg-[#0f0f12]"
            >
              <div className="flex gap-1.5 lg:gap-2 pb-0.5">
                {FILTER_OPTIONS
                  .filter(opt => opt.id !== "following" || hasFollowing)
                  .map((opt) => {
                  const Icon = opt.icon;
                  const isActive = filter === opt.id;
                  const isNewMarket = opt.id === "new";
                  return (
                    <div key={opt.id} className="relative">
                      <button
                        onClick={() => setFilter(opt.id)}
                        className={`flex items-center gap-1.5 lg:gap-2 px-3 lg:px-4 py-[7px] lg:py-2 rounded-[5px] text-[11px] lg:text-xs font-semibold whitespace-nowrap transition-colors duration-200 ${
                          isActive
                            ? "bg-gray-900 dark:bg-white text-white dark:text-gray-900"
                            : "bg-black/[0.04] dark:bg-white/[0.06] text-gray-600 dark:text-gray-400 hover:bg-black/[0.12] dark:hover:bg-white/[0.14]"
                        }`}
                      >
                        <Icon className="w-3.5 h-3.5 lg:w-3.5 lg:h-3.5" />
                        {t(opt.labelKey)}
                        {/* Settings icon for new_market filter */}
                        {isNewMarket && isActive && (
                          <Settings
                            className="w-2.5 h-2.5 ml-0.5 cursor-pointer hover:rotate-90 transition-transform"
                            onClick={(e) => {
                              e.stopPropagation();
                              setShowCategoryFilter(!showCategoryFilter);
                            }}
                          />
                        )}
                      </button>

                      {/* Category filter dropdown for new_market */}
                      {isNewMarket && isActive && showCategoryFilter && (
                      <div className="absolute top-full left-0 mt-2 z-50 bg-white/90 dark:bg-[#18181D]/90 border border-gray-200 dark:border-[#1A1A1A] rounded-[5px] shadow-xl p-3 min-w-[200px]">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-xs font-semibold text-gray-900 dark:text-white">{t("activity.categories")}</span>
                          <button
                            onClick={() => setShowCategoryFilter(false)}
                            className="p-1 rounded-[5px] hover:bg-gray-100 dark:hover:bg-white/[0.06]"
                          >
                            <X className="w-3 h-3 text-gray-400" />
                          </button>
                        </div>
                        <div className="space-y-1">
                          {NEW_MARKET_CATEGORIES.map((cat) => {
                            const isEnabled = enabledCategories.includes(cat.id);
                            return (
                              <button
                                key={cat.id}
                                onClick={() => toggleCategory(cat.id)}
                                className={`w-full flex items-center gap-2 px-2.5 py-1.5 rounded-[5px] text-xs font-medium transition-colors ${
                                  isEnabled
                                    ? "bg-gray-900 dark:bg-white text-white dark:text-gray-900"
                                    : "text-gray-500 hover:bg-gray-100 dark:hover:bg-white/[0.04]"
                                }`}
                              >
                                <span className="flex-1 text-left">{cat.label}</span>
                                {isEnabled && <Check className="w-3 h-3" />}
                              </button>
                            );
                          })}
                        </div>
                        <div className="mt-2 pt-2 border-t border-gray-200 dark:border-[#1A1A1A]">
                          <button
                            onClick={() => setEnabledCategories(NEW_MARKET_CATEGORIES.map(c => c.id))}
                            className="w-full text-[10px] text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
                          >
                            Select all
                          </button>
                        </div>
                      </div>
                    )}
                    </div>
                  );
                })}
              </div>
            </HorizontalScroll>

            {/* Min amount filter — on its own line for whales/fresh filters */}
            {(filter === "whales" || filter === "fresh") && (
              <div className="flex gap-1 overflow-x-auto scrollbar-hide pt-2 pb-0.5">
                {[0, 5000, 10000, 25000, 50000, 100000].map((amt) => (
                  <button
                    key={amt}
                    onClick={() => setMinAmount(amt)}
                    className={`px-2.5 py-1 rounded-[5px] text-[10px] lg:text-[11px] font-semibold whitespace-nowrap transition-colors flex-shrink-0 ${
                      minAmount === amt
                        ? "bg-gray-900 dark:bg-white text-white dark:text-gray-900"
                        : "bg-black/[0.04] dark:bg-white/[0.06] text-gray-500 dark:text-gray-400 hover:bg-black/[0.12] dark:hover:bg-white/[0.14]"
                    }`}
                  >
                    {amt === 0 ? "All" : amt >= 1000 ? `$${amt / 1000}K` : `$${amt}`}
                  </button>
                ))}
              </div>
            )}
          </div>

        </div>

        {/* Spacer for fixed filter bar on mobile. Single row of category
            pills (~3rem). When whales/fresh add the min-amount strip
            inside the bar, grow the spacer so content doesn't slide
            under it. */}
        {!embedded && (
          <div
            className={`lg:hidden ${
              filter === "whales" || filter === "fresh" ? "h-[5.25rem]" : "h-[3rem]"
            }`}
          />
        )}

        {/* Secondary action row — view-mode toggle, anchored right.
            Lives below the filter pills on both mobile and desktop so
            the header bar matches /markets exactly (pure pill scroll,
            no chrome competing for space on the right edge).
            Refresh button removed; the newEvents pill auto-surfaces
            fresh activity. Publish moved to the FAB at bottom-right. */}
        {!embedded && (
          // 2026-05-22 (Pika): added a small "View" label so the
          // toggle reads as "I can switch how the feed renders"
          // instead of two anonymous icons floating against the
          // right edge. Same segmented control style, just labelled.
          <div className="flex items-center gap-2 px-4 lg:px-6 py-2">
            <div className="flex-1" />
            <span className="text-[11px] font-medium text-gray-500 dark:text-gray-400 hidden sm:inline">View</span>
            <div className="flex bg-black/[0.03] dark:bg-white/[0.04] rounded-[5px] p-0.5 flex-shrink-0">
              <button
                onClick={() => setViewMode("expanded")}
                className={`p-1.5 rounded-[3px] transition-colors ${viewMode === "expanded" ? "bg-gray-900 dark:bg-white text-white dark:text-black" : "text-gray-500 dark:text-gray-400 hover:bg-black/[0.06] dark:hover:bg-white/[0.08]"}`}
                title={t("activity.viewToggle.expanded")}
                aria-label={t("activity.viewToggle.expanded")}
              >
                <LayoutGridIcon size={14} className={viewMode === "expanded" ? "text-white dark:text-black" : "text-gray-400"} />
              </button>
              <button
                onClick={() => setViewMode("compact")}
                className={`p-1.5 rounded-[3px] transition-colors ${viewMode === "compact" ? "bg-gray-900 dark:bg-white text-white dark:text-black" : "text-gray-500 dark:text-gray-400 hover:bg-black/[0.06] dark:hover:bg-white/[0.08]"}`}
                title={t("activity.viewToggle.compact")}
                aria-label={t("activity.viewToggle.compact")}
              >
                <LayoutListIcon size={14} className={viewMode === "compact" ? "text-white dark:text-black" : "text-gray-400"} />
              </button>
            </div>
          </div>
        )}

        {/* Click outside to close category filter */}
        {showCategoryFilter && (
          <div
            className="fixed inset-0 z-40"
            onClick={() => setShowCategoryFilter(false)}
          />
        )}

        {/* New activity pill - compact floating indicator */}
        {newEvents.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: -10, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            className="sticky top-2 z-20 flex justify-center pointer-events-none mt-3"
          >
            <button
              onClick={loadNewEvents}
              className="pointer-events-auto inline-flex items-center gap-2 px-4 py-2 bg-gray-900/95 dark:bg-[#18181D]/95 backdrop-blur-md border border-gray-200 dark:border-[#1A1A1A] rounded-[5px] text-sm font-semibold text-white hover:bg-gray-800 dark:hover:bg-[#1e1e24] transition-colors"
            >
              <span className="relative flex h-1.5 w-1.5">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75"></span>
                <span className="relative inline-flex rounded-[5px] h-1.5 w-1.5 bg-white"></span>
              </span>
              <span>{newEvents.length} new {newEvents.length === 1 ? 'activity' : 'activities'}</span>
              <ChevronUp className="w-3.5 h-3.5 text-gray-500" />
            </button>
          </motion.div>
        )}

        {/* Activity Stream */}
        <div className="px-2 lg:pt-4">
          {loading ? (
            <div className="space-y-3">
              {[1, 2, 3, 4, 5].map((i) => (
                <div
                  key={i}
                  className="p-4 bg-white dark:bg-[#18181D] rounded-[5px] animate-pulse"
                >
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 bg-gray-200 dark:bg-[#0f0f12] rounded-[5px]" />
                    <div className="flex-1">
                      <div className="h-4 bg-gray-200 dark:bg-[#0f0f12] rounded w-3/4 mb-2" />
                      <div className="h-3 bg-gray-200 dark:bg-[#0f0f12] rounded w-1/2" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : events.length === 0 ? (
            <div className="py-8">
              {(() => {
                const emptyConfig: Record<string, { icon: React.ReactNode; title: string; desc: string; cta: string; href: string }> = {
                  following: {
                    icon: <Users className="w-6 h-6 text-gray-400 dark:text-gray-500" />,
                    title: t("activity.empty.following.title"),
                    desc: t("activity.empty.following.desc"),
                    cta: t("activity.empty.browseTraders"),
                    href: "/traders",
                  },
                  watchlist: {
                    icon: <Eye className="w-6 h-6 text-gray-400 dark:text-gray-500" />,
                    title: t("activity.empty.watchlist.title"),
                    desc: t("activity.empty.watchlist.desc"),
                    cta: t("activity.empty.findTraders"),
                    href: "/traders",
                  },
                  trades: {
                    icon: <TrendingUp className="w-6 h-6 text-gray-400 dark:text-gray-500" />,
                    title: t("activity.empty.trades.title"),
                    desc: t("activity.empty.trades.desc"),
                    cta: t("activity.empty.exploreMarkets"),
                    href: "/",
                  },
                  copies: {
                    icon: <Copy className="w-6 h-6 text-gray-400 dark:text-gray-500" />,
                    title: t("activity.empty.copies.title"),
                    desc: t("activity.empty.copies.desc"),
                    cta: t("activity.empty.browseTraders"),
                    href: "/traders",
                  },
                  opinions: {
                    icon: <MessageSquare className="w-6 h-6 text-gray-400 dark:text-gray-500" />,
                    title: t("activity.empty.opinions.title"),
                    desc: t("activity.empty.opinions.desc"),
                    cta: t("activity.empty.createPost"),
                    href: "",
                  },
                  whales: {
                    icon: <Wallet className="w-6 h-6 text-gray-400 dark:text-gray-500" />,
                    title: t("activity.empty.whales.title"),
                    desc: t("activity.empty.whales.desc"),
                    cta: t("activity.empty.viewAll"),
                    href: "",
                  },
                  moves: {
                    icon: <Target className="w-6 h-6 text-gray-400 dark:text-gray-500" />,
                    title: t("activity.empty.moves.title"),
                    desc: t("activity.empty.moves.desc"),
                    cta: t("activity.empty.exploreMarkets"),
                    href: "/",
                  },
                  new: {
                    icon: <Plus className="w-6 h-6 text-gray-400 dark:text-gray-500" />,
                    title: t("activity.empty.new.title"),
                    desc: t("activity.empty.new.desc"),
                    cta: t("activity.empty.exploreMarkets"),
                    href: "/",
                  },
                };
                const cfg = emptyConfig[filter] || {
                  icon: <Zap className="w-6 h-6 text-gray-400 dark:text-gray-500" />,
                  title: t("activity.empty.default.title"),
                  desc: t("activity.empty.default.desc"),
                  cta: t("activity.empty.exploreMarkets"),
                  href: "/",
                };
                const isActionCta = filter === "opinions" || filter === "whales";
                return (
                  <div className="flex flex-col items-center text-center py-12 px-4">
                    <div className="w-12 h-12 rounded-[5px] bg-gray-100 dark:bg-white/[0.04] flex items-center justify-center mb-4">
                      {cfg.icon}
                    </div>
                    <h3 className="text-[15px] font-semibold text-gray-900 dark:text-white mb-1">
                      {cfg.title}
                    </h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400 max-w-[280px] mb-5">
                      {cfg.desc}
                    </p>
                    {isActionCta && filter === "opinions" ? (
                      <button
                        onClick={() => setShowCreateModal(true)}
                        className="px-5 py-2.5 text-sm font-semibold rounded-[5px] bg-gray-900 dark:bg-white text-white dark:text-gray-900 hover:bg-gray-800 dark:hover:bg-gray-100 transition-colors"
                      >
                        {cfg.cta}
                      </button>
                    ) : filter !== "whales" ? (
                      <Link
                        href={cfg.href}
                        prefetch={false}
                        className="px-5 py-2.5 text-sm font-semibold rounded-[5px] bg-gray-900 dark:bg-white text-white dark:text-gray-900 hover:bg-gray-800 dark:hover:bg-gray-100 transition-colors"
                      >
                        {cfg.cta}
                      </Link>
                    ) : null}
                  </div>
                );
              })()}
            </div>
          ) : (
            <AnimatePresence mode="popLayout">
              <div className={viewMode === "compact" ? "space-y-0" : "space-y-0"}>
                {/* Composer hint — Twitter-style pseudo-textbox at the top
                    of the feed. Opens CreatePostModal on click. Hidden in
                    embedded mode (homepage widgets) where the user is in a
                    reading-only context. Only shows when logged in — anon
                    visitors get nothing here (the modal would just push
                    them to login). */}
                {!embedded && isLoggedIn && (
                  <button
                    type="button"
                    onClick={() => setShowCreateModal(true)}
                    className="w-full flex items-center gap-3 px-3 py-3 mb-3 rounded-[5px] bg-gray-50 dark:bg-white/[0.03] border border-dashed border-gray-300 dark:border-[#2A2A2A] hover:border-gray-400 dark:hover:border-white/25 hover:bg-gray-100/60 dark:hover:bg-white/[0.05] transition-colors text-left group"
                  >
                    <div className="w-8 h-8 rounded-[5px] bg-gray-100 dark:bg-white/[0.06] flex items-center justify-center flex-shrink-0">
                      <PenSquare className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                    </div>
                    <span className="flex-1 text-sm text-gray-500 dark:text-gray-400 group-hover:text-gray-700 dark:group-hover:text-gray-300 transition-colors">
                      {t("activity.composer.placeholder")}
                    </span>
                    <span className="flex items-center justify-center w-8 h-8 bg-gray-900 dark:bg-white text-white dark:text-gray-900 rounded-[5px] group-hover:bg-gray-800 dark:group-hover:bg-gray-100 transition-colors">
                      <Plus className="w-4 h-4" strokeWidth={2.5} />
                    </span>
                  </button>
                )}
                {(embedded ? events.slice(0, 6) : events).map((event, index) => {
                  // Time-group separators removed: each row carries its own
                  // compact "5h" timestamp, so the "5 hours ago" prose header
                  // was just duplicating noise above every other card.
                  const isFresh = freshIds.has(event.id);
                  return (
                    <Fragment key={event.id}>
                      <div className={isFresh ? "animate-in fade-in slide-in-from-top-2 duration-500" : undefined}>
                        {viewMode === "compact" ? (
                          <CompactEventRow event={event} index={index} onCopy={openCopyModal} onTrade={openTradingModal} />
                        ) : (
                          <ActivityEventCard event={event} index={index} onCopy={openCopyModal} onTrade={openTradingModal} onVote={handleVote} expandedEvents={expandedEvents} toggleEventExpansion={toggleEventExpansion} followedTraders={followedTraders} onToggleCopy={handleToggleCopy} />
                        )}
                      </div>
                    </Fragment>
                  );
                })}

                {/* Infinite scroll sentinel — hidden when embedded */}
                {!embedded && (
                  <div ref={sentinelRef} className="py-4">
                    {loadingMore && (
                      <div className="flex items-center justify-center gap-2 py-4">
                        <div className="w-5 h-5 border-2 border-violet-600 dark:border-gray-400 border-t-transparent rounded-full animate-spin" />
                        <span className="text-sm text-gray-500 dark:text-gray-400">{t("activity.loadingMore")}</span>
                      </div>
                    )}
                    {!hasMore && events.length > 0 && (
                      <div className="text-center py-4">
                        <span className="text-sm text-gray-400 dark:text-gray-500">{t("activity.reachedEnd")}</span>
                      </div>
                    )}
                  </div>
                )}

                {/* View all link when embedded */}
                {embedded && events.length > 0 && (
                  <div className="pt-6 text-center">
                    <Link
                      href="/activity"
                      className="text-sm font-semibold text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
                    >
                      View all activity &rarr;
                    </Link>
                  </div>
                )}
              </div>
            </AnimatePresence>
          )}
        </div>
      </div>

      {/* Floating compose FAB removed — replaced by the inline composer
          hint at the top of the feed (Twitter-style "What's on your mind?"
          button). The hint is more discoverable and self-explanatory. */}
    </>
  );
}

